import { Timer } from '@mui/icons-material';
import { LineChart } from '@mui/x-charts';
import useExplorePageStyles from '../useExplorePageStyles';
import { AnswerWithQid, Question } from '../../../../types';
import useSiteActivityStyles from './useSiteActivityStyles';

/**
 * Interface representing the props for the SiteActivity component.
 *
 * - questions The current full list of questions.
 * - answers The current full list of answers with their question's IDs.
 */
interface SiteActivityProps {
  questions: Question[];
  answers: AnswerWithQid[];
}

/**
 * SiteActivity component renders a site activity tab on the explore page which displays
 * a graph of the site's activity over time.
 *
 * @param questions The current full list of questions.
 * @param answers The current full list of answers with their question's IDs.
 */
const SiteActivity = ({ questions, answers }: SiteActivityProps) => {
  const { classes } = useExplorePageStyles();
  const { classes: siteActivityClasses } = useSiteActivityStyles();

  /**
   * Function to collect buckets of time data based on questions and answers asked
   * in the past four weeks.
   *
   * @param data - The type of data, either questions or answers, to get the time buckets for.
   * @returns a list of the question/answer count asked/answered within each bucket.
   */
  const getTimeData = (dataType: string) => {
    const ret = [0, 0, 0, 0];
    const now = new Date();
    for (const d of dataType === 'question' ? questions : answers) {
      const dDate =
        dataType === 'question' ? (d as Question).askDateTime : (d as AnswerWithQid).ansDateTime;
      const daysApart = (now.getTime() - new Date(dDate).getTime()) / (24 * 60 * 60 * 1000);
      const weeksApart = Math.floor(daysApart / 7);
      if (weeksApart <= 3) {
        const cur = ret[weeksApart];
        ret.splice(weeksApart, 1, cur + 1);
      }
    }
    return ret.reverse();
  };

  const data = [
    { data: getTimeData('question'), label: 'Questions' },
    { data: getTimeData('answer'), label: 'Answers' },
  ];

  /**
   * Function to get the date a given number of weeks ago.
   *
   * @param data - The number of weeks ago the return date should be.
   * @returns a list of the question/answer count asked/answered within each bucket.
   */
  const getXWeeksAgo = (weeks: number) => {
    const now = new Date();
    return new Date(now.getTime() - 24 * 60 * 60 * 1000 * 7 * weeks).toLocaleDateString();
  };

  const xLabels = [getXWeeksAgo(4), getXWeeksAgo(3), getXWeeksAgo(2), getXWeeksAgo(1)];

  return (
    <div className={siteActivityClasses.siteActivityContainerRow}>
      <div className={siteActivityClasses.siteActivityContainerColumn}>
        <div className={classes.titleContainer}>
          <span>Site Activity</span>
          <div style={{ width: '10px' }}></div>
          <Timer />
        </div>
        <LineChart
          series={data}
          xAxis={[{ scaleType: 'point', data: xLabels, label: 'Week of' }]}
          colors={['hsl(211, 100%, 50%)', 'hsl(25, 85%, 50%)']}
        />
      </div>
    </div>
  );
};

export default SiteActivity;
